import { useEffect, useMemo, useState } from "react";
import { Loader2, Maximize2, Minimize2, Share2 } from "lucide-react";
import {
    DndContext, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
    getGardenMap, createBed, updateBed, createStructure, updateStructure,
} from "@/lib/api";
import { MapItem } from "@/components/garden-map/MapItem";
import { StructurePalette } from "@/components/garden-map/StructurePalette";
import { MapItemEditDialog } from "@/components/garden-map/MapItemEditDialog";
import { GardenInfoCard } from "@/components/garden-map/GardenInfoCard";
import { STRUCTURE_TOKENS } from "@/components/garden-map/structure-types";
import ShareGardenDialog from "@/components/ShareGardenDialog";
import { useGardens } from "@/contexts/GardenContext";

const SNAP_CM = 10;

const snap = (val) => Math.round(val / SNAP_CM) * SNAP_CM;

/**
 * Top-down 2D plan of the entire garden — beds + structures + paths.
 * Drag any item to reposition; click to edit; add new items from the palette.
 */
export default function GardenMap() {
    const { activeGardenId, refresh: refreshGardens } = useGardens();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scale, setScale] = useState(0.5); // px per cm
    const [editing, setEditing] = useState(null); // {kind, item}
    const [shareOpen, setShareOpen] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    );

    const load = async () => {
        if (!activeGardenId) {
            setData(null);
            return;
        }
        try {
            setData(await getGardenMap(activeGardenId));
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    useEffect(() => {
        setLoading(true);
        load().finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeGardenId]);

    const garden = data?.garden;
    const beds = data?.beds || [];
    const structures = data?.structures || [];

    const canvasWidth = garden ? garden.length_cm * scale : 800;
    const canvasHeight = garden ? garden.width_cm * scale : 600;

    // ---- Drag end: persist new position ----
    const handleDragEnd = async (event) => {
        const { active, delta } = event;
        if (!delta || (delta.x === 0 && delta.y === 0)) return;
        const { kind, itemId, item } = active.data.current || {};
        if (!kind || !itemId) return;

        const newX = snap(Math.max(0, Math.min(
            (garden?.length_cm || 1000) - (item.length_cm || 100),
            (item.position_x || 0) + delta.x / scale,
        )));
        const newY = snap(Math.max(0, Math.min(
            (garden?.width_cm || 800) - (item.width_cm || 100),
            (item.position_y || 0) + delta.y / scale,
        )));

        try {
            if (kind === "bed") {
                await updateBed(itemId, { position_x: newX, position_y: newY });
            } else {
                await updateStructure(itemId, { position_x: newX, position_y: newY });
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    // ---- Add new item handlers ----
    const centerPos = (lengthCm, widthCm) => ({
        x: Math.max(0, Math.round(((garden?.length_cm || 1000) - lengthCm) / 2)),
        y: Math.max(0, Math.round(((garden?.width_cm || 800) - widthCm) / 2)),
    });

    const addBed = async () => {
        if (!garden) return;
        const L = 240, W = 120;
        const c = centerPos(L, W);
        try {
            const idx = beds.length + 1;
            await createBed({
                garden_id: garden.id,
                name: `Bed ${idx}`,
                length_cm: L, width_cm: W,
                position_x: c.x, position_y: c.y,
            });
            toast.success("Bed added — drag to position");
            await load();
        } catch (e) {
            toast.error(e.message);
        }
    };

    const addStructure = async (type) => {
        if (!garden) return;
        const token = STRUCTURE_TOKENS[type];
        const c = centerPos(token.default_length_cm, token.default_width_cm);
        try {
            await createStructure({
                garden_id: garden.id,
                type,
                position_x: c.x,
                position_y: c.y,
                length_cm: token.default_length_cm,
                width_cm: token.default_width_cm,
                properties: { ...token.default_props },
            });
            toast.success(`${token.label} added — drag to position`);
            await load();
        } catch (e) {
            toast.error(e.message);
        }
    };

    const handleSelect = (kind, item) => setEditing({ kind, item });

    const zoomIn = () => setScale((s) => Math.min(1.2, +(s + 0.1).toFixed(2)));
    const zoomOut = () => setScale((s) => Math.max(0.2, +(s - 0.1).toFixed(2)));

    if (loading) {
        return (
            <div className="text-[var(--vm-text-muted)] flex items-center gap-2" data-testid="garden-map-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading garden map…
            </div>
        );
    }
    if (!garden) {
        return <div data-testid="garden-map-empty">No garden found.</div>;
    }

    return (
        <div className="space-y-8" data-testid="garden-map-page">
            <PageHeader
                overline="Top-down plan"
                title="Garden Map"
                description="Drag any bed or structure to position it. Click to edit. Add new items from the palette."
                action={
                    <Button
                        onClick={() => setShareOpen(true)}
                        data-testid="garden-map-share-button"
                        variant="outline"
                        className="rounded-full border-[var(--vm-border)]"
                    >
                        <Share2 className="w-4 h-4 mr-2" />
                        {garden?.share_token ? "Sharing on" : "Share garden"}
                    </Button>
                }
            />

            <GardenInfoCard garden={garden} onSaved={async () => { await refreshGardens(); await load(); }} />

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Canvas */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-xs text-[var(--vm-text-muted)] font-mono-grid">
                            scale: {scale.toFixed(1)} px/cm · {beds.length} beds · {structures.length} structures · grid snap {SNAP_CM} cm
                        </div>
                        <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={zoomOut} data-testid="map-zoom-out"
                                    className="h-8 w-8"><Minimize2 className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="ghost" onClick={zoomIn} data-testid="map-zoom-in"
                                    className="h-8 w-8"><Maximize2 className="w-3.5 h-3.5" /></Button>
                        </div>
                    </div>
                    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                        <div
                            className="relative rounded-lg border-2 border-dashed border-[var(--vm-border)] bg-[var(--vm-input)]/30 overflow-auto"
                            style={{ minHeight: 200 }}
                            data-testid="garden-map-canvas"
                        >
                            <div
                                className="relative bg-[#F5EFDF] mx-auto"
                                style={{
                                    width: canvasWidth,
                                    height: canvasHeight,
                                    backgroundImage:
                                        "linear-gradient(to right, rgba(140,124,90,0.10) 1px, transparent 1px), " +
                                        "linear-gradient(to bottom, rgba(140,124,90,0.10) 1px, transparent 1px)",
                                    backgroundSize: `${100 * scale}px ${100 * scale}px`,
                                }}
                            >
                                {/* Compass / north indicator */}
                                <div className="absolute top-2 left-2 text-[10px] text-[var(--vm-text-muted)] font-mono-grid pointer-events-none select-none">N ↑</div>

                                {/* Garden dimensions label */}
                                <div className="absolute bottom-2 right-2 text-[10px] text-[var(--vm-text-muted)] font-mono-grid pointer-events-none select-none">
                                    {(garden.length_cm / 100).toFixed(1)} × {(garden.width_cm / 100).toFixed(1)} m
                                </div>

                                {/* Render structures first (behind beds) */}
                                {structures.map((s) => (
                                    <MapItem
                                        key={s.id}
                                        kind="structure"
                                        item={s}
                                        scale={scale}
                                        selected={editing?.kind === "structure" && editing.item.id === s.id}
                                        onClick={() => handleSelect("structure", s)}
                                    />
                                ))}

                                {/* Beds on top */}
                                {beds.map((b) => (
                                    <MapItem
                                        key={b.id}
                                        kind="bed"
                                        item={b}
                                        scale={scale}
                                        selected={editing?.kind === "bed" && editing.item.id === b.id}
                                        onClick={() => handleSelect("bed", b)}
                                    />
                                ))}
                            </div>
                        </div>
                    </DndContext>
                </div>

                <StructurePalette
                    onAddBed={addBed}
                    onAddStructure={addStructure}
                />
            </div>

            {/* Edit dialog */}
            {editing && (
                <MapItemEditDialog
                    kind={editing.kind}
                    item={editing.item}
                    open={!!editing}
                    onOpenChange={(o) => { if (!o) setEditing(null); }}
                    onSaved={load}
                    onDeleted={load}
                />
            )}

            {/* Share dialog */}
            <ShareGardenDialog
                open={shareOpen}
                onOpenChange={setShareOpen}
                garden={garden}
                onChange={load}
            />
        </div>
    );
}
